/**
 * CHARSTAN i18n Configuration
 * M7B-P1: i18n Foundation
 * 
 * Architecture:
 * - Namespace-scoped JSON files
 * - Browser language detection with localStorage override
 * - Fallback: English (en)
 * - Supported: English (en), Portuguese Brazil (pt-BR)
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import EN namespace files
import enCommon from './locales/en/common.json';
import enNav from './locales/en/nav.json';
import enAuth from './locales/en/auth.json';
import enScriber from './locales/en/scriber.json';
import enAudit from './locales/en/audit.json';
import enDashboard from './locales/en/dashboard.json';
import enValidation from './locales/en/validation.json';
import enLanguages from './locales/en/languages.json';
import enTrust from './locales/en/trust.json';
import enGovernance from './locales/en/governance.json';
import enScar from './locales/en/scar.json';
import enCapa from './locales/en/capa.json';
import enOperations from './locales/en/operations.json';
import enModules from './locales/en/modules.json';
import enPlatform from './locales/en/platform.json';
import enGuide from './locales/en/guide.json';

// Import PT-BR namespace files
import ptBRCommon from './locales/pt-BR/common.json';
import ptBRNav from './locales/pt-BR/nav.json';
import ptBRAuth from './locales/pt-BR/auth.json';
import ptBRScriber from './locales/pt-BR/scriber.json';
import ptBRAudit from './locales/pt-BR/audit.json';
import ptBRDashboard from './locales/pt-BR/dashboard.json';
import ptBRValidation from './locales/pt-BR/validation.json';
import ptBRLanguages from './locales/pt-BR/languages.json';
import ptBRTrust from './locales/pt-BR/trust.json';
import ptBRGovernance from './locales/pt-BR/governance.json';
import ptBRScar from './locales/pt-BR/scar.json';
import ptBRCapa from './locales/pt-BR/capa.json';
import ptBROperations from './locales/pt-BR/operations.json';
import ptBRModules from './locales/pt-BR/modules.json';
import ptBRPlatform from './locales/pt-BR/platform.json';
import ptBRGuide from './locales/pt-BR/guide.json';
import enReader from './locales/en/reader.json';
import ptBRReader from './locales/pt-BR/reader.json';
import enTrial from './locales/en/trial.json';
import ptBRTrial from './locales/pt-BR/trial.json';

// Import zh-CN namespace files (Simplified Chinese — mainland market).
// Seeded as EN passthrough until the LLM-draft translator + SME review
// runs via `POST /api/i18n/zh-cn/translate`. Users see a "DRAFT translation
// pending SME review" banner when this locale is selected.
import zhCNCommon from './locales/zh-CN/common.json';
import zhCNNav from './locales/zh-CN/nav.json';
import zhCNAuth from './locales/zh-CN/auth.json';
import zhCNScriber from './locales/zh-CN/scriber.json';
import zhCNAudit from './locales/zh-CN/audit.json';
import zhCNDashboard from './locales/zh-CN/dashboard.json';
import zhCNValidation from './locales/zh-CN/validation.json';
import zhCNLanguages from './locales/zh-CN/languages.json';
import zhCNTrust from './locales/zh-CN/trust.json';
import zhCNGovernance from './locales/zh-CN/governance.json';
import zhCNScar from './locales/zh-CN/scar.json';
import zhCNCapa from './locales/zh-CN/capa.json';
import zhCNOperations from './locales/zh-CN/operations.json';
import zhCNModules from './locales/zh-CN/modules.json';
import zhCNPlatform from './locales/zh-CN/platform.json';
import zhCNGuide from './locales/zh-CN/guide.json';
import zhCNReader from './locales/zh-CN/reader.json';
import zhCNTrial from './locales/zh-CN/trial.json';

// Import zh-TW namespace files (Traditional Chinese — Taiwan market).
// Bootstrapped via PR_RUN_049 using P. Chang's WI glossary
// (`frontend/src/i18n/glossaries/zh-TW.json`). REVIEWED 2026-05-27 by
// P. Chang + Taiwan + Hong Kong reviewer panels — sign-off recorded in
// `locales/zh-TW/_translation_status.json` (PR_RUN_051). No string-level
// edits requested; the bootstrap output was approved as-is.
import zhTWCommon from './locales/zh-TW/common.json';
import zhTWNav from './locales/zh-TW/nav.json';
import zhTWAuth from './locales/zh-TW/auth.json';
import zhTWScriber from './locales/zh-TW/scriber.json';
import zhTWAudit from './locales/zh-TW/audit.json';
import zhTWDashboard from './locales/zh-TW/dashboard.json';
import zhTWValidation from './locales/zh-TW/validation.json';
import zhTWLanguages from './locales/zh-TW/languages.json';
import zhTWTrust from './locales/zh-TW/trust.json';
import zhTWGovernance from './locales/zh-TW/governance.json';
import zhTWScar from './locales/zh-TW/scar.json';
import zhTWCapa from './locales/zh-TW/capa.json';
import zhTWOperations from './locales/zh-TW/operations.json';
import zhTWModules from './locales/zh-TW/modules.json';
import zhTWPlatform from './locales/zh-TW/platform.json';
import zhTWGuide from './locales/zh-TW/guide.json';
import zhTWReader from './locales/zh-TW/reader.json';
import zhTWTrial from './locales/zh-TW/trial.json';

// Import zh-HK namespace files (Traditional Chinese — Hong Kong market).
// Currently a clone of zh-TW; HK-specific deltas live in
// `glossaries/zh-TW.json::zh_hk_specific_overrides` and re-applied via
// `scripts/bootstrap_zh_tw_zh_hk.py --locale zh-HK`. REVIEWED 2026-05-27
// by P. Chang + Taiwan + Hong Kong reviewer panels (PR_RUN_051).
import zhHKCommon from './locales/zh-HK/common.json';
import zhHKNav from './locales/zh-HK/nav.json';
import zhHKAuth from './locales/zh-HK/auth.json';
import zhHKScriber from './locales/zh-HK/scriber.json';
import zhHKAudit from './locales/zh-HK/audit.json';
import zhHKDashboard from './locales/zh-HK/dashboard.json';
import zhHKValidation from './locales/zh-HK/validation.json';
import zhHKLanguages from './locales/zh-HK/languages.json';
import zhHKTrust from './locales/zh-HK/trust.json';
import zhHKGovernance from './locales/zh-HK/governance.json';
import zhHKScar from './locales/zh-HK/scar.json';
import zhHKCapa from './locales/zh-HK/capa.json';
import zhHKOperations from './locales/zh-HK/operations.json';
import zhHKModules from './locales/zh-HK/modules.json';
import zhHKPlatform from './locales/zh-HK/platform.json';
import zhHKGuide from './locales/zh-HK/guide.json';
import zhHKReader from './locales/zh-HK/reader.json';
import zhHKTrial from './locales/zh-HK/trial.json';

// LocalStorage key for language preference
const LOCALE_STORAGE_KEY = 'charstan_locale';

// Supported languages
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸', shortName: 'EN' },
  { code: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷', shortName: 'PT' },
  { code: 'zh-CN', name: '简体中文', flag: '🇨🇳', shortName: 'ZH' },
  { code: 'zh-TW', name: '繁體中文 (台灣)', flag: '🇹🇼', shortName: 'TW' },
  { code: 'zh-HK', name: '繁體中文 (香港)', flag: '🇭🇰', shortName: 'HK' },
];

// Get initial language
const getInitialLanguage = () => {
  // 1. Check URL query param first (e.g., ?lang=pt-BR) — used by
  //    QR codes / marketing links that need to deterministically force a
  //    locale regardless of the attendee's browser default. Persist to
  //    localStorage so subsequent navigation within the session keeps
  //    the chosen language.
  try {
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang') || params.get('locale');
    if (urlLang) {
      // Accept exact match ("pt-BR") or base match ("pt" -> "pt-BR")
      const exact = SUPPORTED_LANGUAGES.find(l => l.code === urlLang);
      const base = !exact && SUPPORTED_LANGUAGES.find(l => l.code.split('-')[0] === urlLang.split('-')[0]);
      const picked = exact?.code || base?.code;
      if (picked) {
        try { localStorage.setItem(LOCALE_STORAGE_KEY, picked); } catch { /* ignore */ }
        return picked;
      }
    }
  } catch { /* SSR or permission-denied storage — fall through */ }

  // 2. Check localStorage
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && SUPPORTED_LANGUAGES.some(l => l.code === stored)) {
    return stored;
  }
  
  // 3. Check browser language
  const browserLang = navigator.language || navigator.userLanguage;
  if (browserLang) {
    // Exact match
    if (SUPPORTED_LANGUAGES.some(l => l.code === browserLang)) {
      return browserLang;
    }
    // Partial match (e.g., 'pt' matches 'pt-BR')
    const langBase = browserLang.split('-')[0];
    const match = SUPPORTED_LANGUAGES.find(l => l.code.startsWith(langBase));
    if (match) {
      return match.code;
    }
  }
  
  // 4. Default to English
  return 'en';
};

// Combined resources
const resources = {
  en: {
    common: enCommon,
    nav: enNav,
    auth: enAuth,
    scriber: enScriber,
    audit: enAudit,
    dashboard: enDashboard,
    validation: enValidation,
    languages: enLanguages,
    trust: enTrust,
    governance: enGovernance,
    scar: enScar,
    capa: enCapa,
    operations: enOperations,
    modules: enModules,
    platform: enPlatform,
    guide: enGuide,
    reader: enReader,
    trial: enTrial,
  },
  'pt-BR': {
    common: ptBRCommon,
    nav: ptBRNav,
    auth: ptBRAuth,
    scriber: ptBRScriber,
    audit: ptBRAudit,
    dashboard: ptBRDashboard,
    validation: ptBRValidation,
    languages: ptBRLanguages,
    trust: ptBRTrust,
    governance: ptBRGovernance,
    scar: ptBRScar,
    capa: ptBRCapa,
    operations: ptBROperations,
    modules: ptBRModules,
    platform: ptBRPlatform,
    guide: ptBRGuide,
    reader: ptBRReader,
    trial: ptBRTrial,
  },
  'zh-CN': {
    common: zhCNCommon,
    nav: zhCNNav,
    auth: zhCNAuth,
    scriber: zhCNScriber,
    audit: zhCNAudit,
    dashboard: zhCNDashboard,
    validation: zhCNValidation,
    languages: zhCNLanguages,
    trust: zhCNTrust,
    governance: zhCNGovernance,
    scar: zhCNScar,
    capa: zhCNCapa,
    operations: zhCNOperations,
    modules: zhCNModules,
    platform: zhCNPlatform,
    guide: zhCNGuide,
    reader: zhCNReader,
    trial: zhCNTrial,
  },
  'zh-TW': {
    common: zhTWCommon,
    nav: zhTWNav,
    auth: zhTWAuth,
    scriber: zhTWScriber,
    audit: zhTWAudit,
    dashboard: zhTWDashboard,
    validation: zhTWValidation,
    languages: zhTWLanguages,
    trust: zhTWTrust,
    governance: zhTWGovernance,
    scar: zhTWScar,
    capa: zhTWCapa,
    operations: zhTWOperations,
    modules: zhTWModules,
    platform: zhTWPlatform,
    guide: zhTWGuide,
    reader: zhTWReader,
    trial: zhTWTrial,
  },
  'zh-HK': {
    common: zhHKCommon,
    nav: zhHKNav,
    auth: zhHKAuth,
    scriber: zhHKScriber,
    audit: zhHKAudit,
    dashboard: zhHKDashboard,
    validation: zhHKValidation,
    languages: zhHKLanguages,
    trust: zhHKTrust,
    governance: zhHKGovernance,
    scar: zhHKScar,
    capa: zhHKCapa,
    operations: zhHKOperations,
    modules: zhHKModules,
    platform: zhHKPlatform,
    guide: zhHKGuide,
    reader: zhHKReader,
    trial: zhHKTrial,
  },
};

// Initialize i18next
i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialLanguage(),
    fallbackLng: 'en',
    supportedLngs: ['en', 'pt-BR', 'zh-CN', 'zh-TW', 'zh-HK'],
    
    // Default namespace
    defaultNS: 'common',
    
    // Available namespaces
    ns: ['common', 'nav', 'auth', 'scriber', 'audit', 'dashboard', 'validation', 'languages', 'trust', 'governance', 'scar', 'capa', 'operations', 'modules', 'platform', 'guide', 'reader', 'trial'],
    
    // Interpolation config
    interpolation: {
      escapeValue: false, // React already escapes by default
    },
    
    // React-specific options
    react: {
      useSuspense: false, // Disable suspense for SSR compatibility
    },
    
    // Missing key handling (development mode)
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (lngs, ns, key, fallbackValue) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[i18n] Missing key: ${ns}:${key} for languages: ${lngs.join(', ')}`);
      }
    },
    
    // Return key if not found (development safety net)
    returnEmptyString: false,
  });

// Language change handler - persists to localStorage
i18n.on('languageChanged', (lng) => {
  localStorage.setItem(LOCALE_STORAGE_KEY, lng);
  // Update document lang attribute for accessibility
  document.documentElement.lang = lng;
});

// Set initial document lang
document.documentElement.lang = i18n.language;

export default i18n;
